import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Mail, Eye, Send, AlertTriangle, CheckCircle2, Loader2, History,
  Search, Plus, X, Users, UserMinus, RefreshCw,
} from 'lucide-react';
import apiClient from '../utils/apiClient';
import { formatDateTime } from '../utils/datetime';
import SandboxedHtmlFrame from '../components/SandboxedHtmlFrame';

/**
 * Bulk Email — operator-facing composer (v1.1, ESS-style).
 *
 * Same composer handles 1-to-1 sends AND mass blasts:
 *   - Operator picks a Filter (Active / Delinquent / All / Archived)
 *   - Searchable customer picker + "+ Add" to add one at a time
 *   - "Add All" pulls in everyone matching the current Filter
 *   - "Remove All" clears the list
 *   - "Selected recipients (N)" panel shows current picks with X to drop
 *   - Subject + HTML body (with merge tag chips) + optional plain text
 *   - Preview renders the email for the first selected recipient
 *   - Send pushes the explicit customer_ids list to the backend
 *
 * Test workflow: add just yourself in the picker, fill subject + body,
 * click Send. No separate "test mode" — the same composer is the test.
 *
 * Merge tags rendered server-side:
 *   {{tenant_name}}, {{facility_name}}, {{facility_phone}},
 *   {{unit_number}}, {{customer_number}}
 *
 * Hosted under Settings > Communication > Email (see
 * CommunicationSection.js, embedded inside Settings.js as of
 * 2026-06-01). Permission gate: UPDATE_FACILITY (staff and above).
 */

const FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'delinquent', label: 'Delinquent' },
  { key: 'all', label: 'All' },
  { key: 'archived', label: 'Archived' },
];

const MERGE_TAGS = [
  '{{tenant_name}}',
  '{{facility_name}}',
  '{{facility_phone}}',
  '{{unit_number}}',
  '{{customer_number}}',
];

/* view: 'both' keeps the original single-page layout; 'compose' renders only
 * the send surface (Manual -> Email); 'log' renders only Recent sends
 * (the Email log tab). Split at the operator's request: the log is a
 * lookup surface ("did it go out?") and composing is an action — mixing
 * them buried the log under a long form. */
const BulkEmail = ({ facility, view = 'both' }) => {
  const facilityId = facility?._id || facility?.id;
  const timeZone = facility?.timezone || undefined;

  // ── Recipient picker state ────────────────────────────────────────
  const [filterKey, setFilterKey] = useState('active');
  const [search, setSearch] = useState('');
  const [available, setAvailable] = useState([]);   // server-returned customers matching filter+search
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableError, setAvailableError] = useState(null);
  const [pickedId, setPickedId] = useState('');     // currently-highlighted in dropdown
  const [selected, setSelected] = useState([]);     // {_id, full_name, email, customer_number}
  const debounceRef = useRef(null);

  // ── Composer state ────────────────────────────────────────────────
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');
  // Each entry: { filename, content_base64, content_type, size }
  // We hold base64 here so the same encoded payload travels in the
  // preview-payload-shape AND the send payload without re-reading the
  // file. Browser-side cap: 8 MB per file to stay well under Resend's
  // 40 MB per-email ceiling and avoid blocking the UI on large reads.
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState(null);
  const ATTACHMENT_SIZE_CAP_BYTES = 8 * 1024 * 1024;
  // Body authoring mode. "plain" wraps line breaks for the recipient
  // (operator types like a normal email; backend converts \n -> <br>).
  // "html" passes the body through as-is (operator pastes HTML directly).
  // Default = "plain" because most operators type, not author HTML.
  const [bodyMode, setBodyMode] = useState('plain');

  // Breadcrumb describing where the compose state came from (e.g. a
  // custom template handoff via sessionStorage). Purely informational —
  // operators can freely edit subject/body after the handoff applies.
  const [composeSource, setComposeSource] = useState(null);

  // ── Preview / send state ──────────────────────────────────────────
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [resendBanner, setResendBanner] = useState(null);
  const [resendModal, setResendModal] = useState(null);
  const [resendModalLoading, setResendModalLoading] = useState(false);

  // ── Customer picker fetch ─────────────────────────────────────────
  const loadAvailable = useCallback(async (f, q) => {
    if (!facilityId) return;
    setAvailableLoading(true);
    setAvailableError(null);
    try {
      const params = new URLSearchParams();
      params.set('filter', f);
      if (q) params.set('search', q);
      params.set('limit', '500');
      const r = await apiClient.get(
        `/api/facilities/${facilityId}/bulk-email/customers?${params.toString()}`
      );
      setAvailable(r.data || []);
    } catch (e) {
      setAvailableError(e?.response?.data?.detail || e.message);
      setAvailable([]);
    } finally {
      setAvailableLoading(false);
    }
  }, [facilityId]);

  // Initial load + reload when filter changes.
  useEffect(() => {
    loadAvailable(filterKey, '');
  }, [loadAvailable, filterKey]);

  // Debounced search refetch (250ms).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadAvailable(filterKey, search.trim());
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, filterKey, loadAvailable]);

  // ── History ───────────────────────────────────────────────────────
  // Viewing what was actually sent. The table showed that an email fired but
  // never what it said, so answering "what did you send me?" meant digging in
  // the Resend dashboard.
  const [bodyView, setBodyView] = useState(null);

  const openBody = async (row) => {
    setBodyView({ loading: true, subject: row.subject });
    try {
      const { data } = await apiClient.get(
        `/api/facilities/${facilityId}/bulk-email/history/${row._id}/body`,
        { params: { kind: row.kind || 'manual' } },
      );
      setBodyView({ ...data, loading: false });
    } catch (e) {
      setBodyView({
        loading: false, subject: row.subject, body: null, body_available: false,
        error: e?.response?.data?.detail || 'Could not load this email',
      });
    }
  };

  // Delivery-tracking posture for THIS facility, computed from its own rows
  // now (active / not receiving / stalled) — so a facility whose provider
  // webhook was never wired doesn't silently read "sent" forever.
  const [tracking, setTracking] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!facilityId) return;
    setHistoryLoading(true);
    try {
      const r = await apiClient.get(`/api/facilities/${facilityId}/bulk-email/history?limit=6000&days=90`);
      setHistory(r.data || []);
    } catch (e) {
      // History is non-critical; log and move on.
      // eslint-disable-next-line no-console
      console.error('bulk email history load failed', e);
    } finally {
      setHistoryLoading(false);
    }
    try {
      const t = await apiClient.get(`/api/facilities/${facilityId}/email-delivery/status`);
      setTracking(t.data || null);
    } catch { /* the tracking banner is best-effort */ }
  }, [facilityId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ── Custom-template handoff (mount-only) ──────────────────────────
  // When the operator clicks "Use" on a custom template in the Templates
  // page, that page stashes a JSON blob in sessionStorage under the
  // `customTemplateHandoff` key and navigates here. We consume it once
  // on mount and clear it so a page refresh doesn't re-apply.
  //
  // Intentionally separate from the Resend Failed flow (which uses
  // setResendModal): this only seeds the fresh compose state.
  useEffect(() => {
    const raw = sessionStorage.getItem('customTemplateHandoff');
    if (!raw) return;
    try {
      const handoff = JSON.parse(raw);
      setSubject(handoff.subject || '');
      setBodyHtml(handoff.body_html || '');
      setBodyText(handoff.body_text || '');
      // Custom templates author HTML; preserve their formatting verbatim
      // instead of running the plain-text paragraph wrapper over it.
      setBodyMode('html');
      if (handoff.template_name) {
        setComposeSource({ kind: 'custom_template', name: handoff.template_name });
      }
      // Fetch the attachment (if any) and pre-add to attachments state.
      if (handoff.attachment_fetch_url && handoff.attachment_filename) {
        (async () => {
          try {
            const res = await apiClient.get(handoff.attachment_fetch_url, {
              responseType: 'blob',
            });
            const blob = res.data;
            // Convert blob -> base64 (strip the data-URL prefix so the
            // shape matches the existing fileToBase64() output).
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result || '';
                const comma = result.indexOf(',');
                resolve(comma >= 0 ? result.slice(comma + 1) : result);
              };
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(blob);
            });
            setAttachments((prev) => [
              ...prev,
              {
                filename: handoff.attachment_filename,
                content_base64: base64,
                content_type: blob.type || 'application/octet-stream',
                size: blob.size,
              },
            ]);
          } catch (e) {
            setAttachmentError(
              `Could not load template attachment: ${e?.response?.data?.detail || e.message}`,
            );
          }
        })();
      }
      // Consume the handoff so a page refresh doesn't re-apply it.
      sessionStorage.removeItem('customTemplateHandoff');
    } catch (_e) {
      // Malformed handoff: drop it silently.
      sessionStorage.removeItem('customTemplateHandoff');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resend Failed (Step 1) — fetch the preview so the operator can review
  // exactly who is about to be re-emailed + the rendered subject/body.
  // The actual send doesn't happen until they confirm in the modal.
  const onResendFailed = useCallback(async (h) => {
    if (!facilityId || !h?._id) return;
    setResendBanner(null);
    setResendModalLoading(true);
    setResendModal({ bulkId: h._id, parentSubject: h.subject, loading: true });
    try {
      const r = await apiClient.get(
        `/api/facilities/${facilityId}/bulk-email/${h._id}/resend-failed/preview`
      );
      setResendModal({ bulkId: h._id, parentSubject: h.subject, ...r.data });
    } catch (e) {
      setResendModal(null);
      setResendBanner({
        kind: 'err',
        text: e?.response?.data?.detail || e.message || 'Preview failed',
      });
    } finally {
      setResendModalLoading(false);
    }
  }, [facilityId]);

  // Resend Failed (Step 2) — operator confirmed; POST the actual retry.
  const onConfirmResend = useCallback(async () => {
    if (!facilityId || !resendModal?.bulkId) return;
    const bulkId = resendModal.bulkId;
    setResendingId(bulkId);
    try {
      const r = await apiClient.post(
        `/api/facilities/${facilityId}/bulk-email/${bulkId}/resend-failed`
      );
      const d = r.data || {};
      setResendBanner({
        kind: 'ok',
        text: `Re-sent: ${d.sent_count}/${d.recipient_count} delivered` +
          (d.skipped_synthetic ? `, ${d.skipped_synthetic} synthetic skipped` : '') +
          (d.failed_count ? `, ${d.failed_count} still failed` : ''),
      });
      setResendModal(null);
      loadHistory();
    } catch (e) {
      setResendBanner({
        kind: 'err',
        text: e?.response?.data?.detail || e.message || 'Retry failed',
      });
    } finally {
      setResendingId(null);
    }
  }, [facilityId, resendModal, loadHistory]);

  // ── Picker helpers ────────────────────────────────────────────────
  const selectedIds = useMemo(() => new Set(selected.map((s) => s._id)), [selected]);
  const availableNotPicked = useMemo(
    () => available.filter((c) => !selectedIds.has(c._id)),
    [available, selectedIds]
  );

  const addOne = (id) => {
    const target = id || pickedId;
    if (!target) return;
    const c = available.find((x) => x._id === target);
    if (!c) return;
    if (selectedIds.has(c._id)) return;
    setSelected((prev) => [...prev, c]);
    setPickedId('');
    setPreview(null);  // invalidate preview after recipient change
  };

  // No batching button: the send pipeline itself paces every email under
  // the provider rate limit (notification_service pacer), so chunking by
  // hand added clicks without adding safety. Removed 2026-07-29.
  const addAll = () => {
    if (availableNotPicked.length === 0) return;
    setSelected((prev) => [...prev, ...availableNotPicked]);
    setPickedId('');
    setPreview(null);
  };

  const removeOne = (id) => {
    setSelected((prev) => prev.filter((c) => c._id !== id));
    setPreview(null);
  };

  const removeAll = () => {
    setSelected([]);
    setPreview(null);
  };

  // ── Preview / send ────────────────────────────────────────────────
  const onPreview = async () => {
    if (!facilityId) return;
    setPreviewError(null);
    setPreview(null);
    setPreviewing(true);
    try {
      const r = await apiClient.post(`/api/facilities/${facilityId}/bulk-email/preview`, {
        customer_ids: selected.map((c) => c._id),
        subject,
        body_html: buildBodyHtml(),
        body_text: bodyText || null,
      });
      setPreview(r.data);
    } catch (e) {
      setPreviewError(e?.response?.data?.detail || e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const openConfirm = () => {
    setSendError(null);
    setSendResult(null);
    setConfirmOpen(true);
  };

  // When operator authored in "plain" mode, wrap the body so line
  // breaks survive HTML rendering. Newlines -> <br>, blank lines ->
  // paragraph breaks, and basic HTML-escape so users typing < > & don't
  // create accidental markup. Merge tags ({{name}}) stay literal because
  // the curly braces escape harmlessly and Resend's render runs after.
  const escapeHtml = (s) =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const buildBodyHtml = () => {
    if (bodyMode === 'html') return bodyHtml;
    // plain mode: escape, then wrap each paragraph in <p> with EXPLICIT
    // margin styles. Some email clients (Gmail dark mode) and our
    // own preview iframe reset <p> margins via Tailwind preflight, so
    // the spacing disappears unless we inline it. Inline styles win.
    // Single \n inside a paragraph -> <br>.
    const paragraphs = bodyHtml.split(/\n{2,}/);
    return paragraphs
      .map(
        (p) =>
          `<p style="margin: 0 0 16px 0; line-height: 1.5;">${escapeHtml(p).replace(/\n/g, '<br>')}</p>`,
      )
      .join('\n');
  };

  // Convert a File to base64 (no data-URL prefix). Used by the
  // attachment picker; runs in the browser and never persists the
  // bytes anywhere other than React state.
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || '';
        // result is "data:<mime>;base64,<payload>" — strip the prefix.
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const onAttachmentPick = async (e) => {
    setAttachmentError(null);
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const next = [];
    for (const f of files) {
      if (f.size > ATTACHMENT_SIZE_CAP_BYTES) {
        setAttachmentError(
          `"${f.name}" is ${(f.size / 1024 / 1024).toFixed(1)} MB — files must be under 8 MB.`,
        );
        continue;
      }
      const content_base64 = await fileToBase64(f);
      next.push({
        filename: f.name,
        content_base64,
        content_type: f.type || null,
        size: f.size,
      });
    }
    setAttachments((prev) => [...prev, ...next]);
    // Reset input so picking the same file twice in a row still fires.
    e.target.value = '';
  };

  const removeAttachment = (i) =>
    setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  const onSend = async () => {
    if (!facilityId) return;
    setSending(true);
    setSendError(null);
    try {
      const r = await apiClient.post(`/api/facilities/${facilityId}/bulk-email/send`, {
        customer_ids: selected.map((c) => c._id),
        subject,
        body_html: buildBodyHtml(),
        body_text: bodyText || null,
        attachments: attachments.length
          ? attachments.map((a) => ({
              filename: a.filename,
              content_base64: a.content_base64,
              content_type: a.content_type,
            }))
          : null,
      });
      setSendResult(r.data);
      setConfirmOpen(false);
      // Refresh history with the new row.
      loadHistory();
      // Delivery confirmations arrive from Resend's webhook a second or two
      // AFTER the send, so the refresh above always captures the row in its
      // pre-delivery state — it showed "pending" forever until the operator
      // happened to reload. Re-poll briefly to catch the confirmation.
      [3000, 8000, 20000].forEach((ms) => setTimeout(loadHistory, ms));
    } catch (e) {
      setSendError(e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  // ── Inject merge tag into the body at caret position ──────────────
  const bodyRef = useRef(null);
  const insertTag = (tag) => {
    const el = bodyRef.current;
    if (!el) {
      setBodyHtml((prev) => prev + tag);
      return;
    }
    const start = el.selectionStart ?? bodyHtml.length;
    const end = el.selectionEnd ?? bodyHtml.length;
    const next = bodyHtml.slice(0, start) + tag + bodyHtml.slice(end);
    setBodyHtml(next);
    // Re-focus + place caret after the inserted tag.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + tag.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const recipientCount = selected.length;
  const canPreview = !!subject.trim() && !!bodyHtml.trim() && recipientCount > 0 && !previewing;
  const canSend = canPreview && !sending;

  return (
    <div className="space-y-6 max-w-4xl">
      {view !== 'log' && (<>
      {/* Recipient picker */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <div className="flex items-start gap-2">
          <Users className="h-4 w-4 text-blue-600 mt-1" />
          <div>
            <h2 className="text-[15px] font-semibold text-slate-900">Recipients</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">
              Pick individuals or click <strong>Add All</strong> to send to everyone matching the current filter.
              To test, add just your own email and send.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">Filter customers</label>
            <select
              value={filterKey}
              onChange={(e) => setFilterKey(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500"
            >
              {FILTERS.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">Search</label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-2.5" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, email, phone, customer #"
                className="w-full border border-slate-300 rounded-md pl-8 pr-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-[12px] font-medium text-slate-700 mb-1">
            Select customers to add{' '}
            <span className="text-slate-400 font-normal">
              ({availableLoading ? 'loading...' : `${availableNotPicked.length} match${availableNotPicked.length === 1 ? '' : 'es'}`})
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            <select
              value={pickedId}
              onChange={(e) => setPickedId(e.target.value)}
              disabled={availableLoading}
              className="flex-1 min-w-[260px] border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500 disabled:bg-slate-50"
            >
              <option value="">— pick a customer —</option>
              {availableNotPicked.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.full_name || '(no name)'}
                  {c.customer_number ? ` · ${c.customer_number}` : ''}
                  {c.email ? ` · ${c.email}` : ' · (no email)'}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => addOne()}
              disabled={!pickedId}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md text-[12px] font-medium"
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
            <button
              type="button"
              onClick={addAll}
              disabled={availableLoading || availableNotPicked.length === 0}
              title={`Adds all ${availableNotPicked.length} matching recipients. Sends are automatically paced under the provider rate limit.`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md text-[12px] font-medium"
            >
              <Users className="h-3.5 w-3.5" />
              Add all ({availableNotPicked.length})
            </button>
            <button
              type="button"
              onClick={removeAll}
              disabled={selected.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-40 text-slate-700 rounded-md text-[12px] font-medium"
            >
              <UserMinus className="h-3.5 w-3.5" /> Remove all
            </button>
          </div>
          {availableError && (
            <div className="mt-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {availableError}
            </div>
          )}
        </div>

        {/* Selected recipients panel */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-[12px] font-medium text-slate-700">
              Selected recipients ({recipientCount})
            </label>
            {recipientCount > 0 && (
              <button
                type="button"
                onClick={removeAll}
                className="text-[11px] text-slate-500 hover:text-rose-600"
              >
                Clear
              </button>
            )}
          </div>
          {recipientCount === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
              No recipients selected. Use the dropdown above or <em>Add all</em>.
            </div>
          ) : (
            <ul className="rounded-md border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto">
              {selected.map((c) => (
                <li key={c._id} className="flex items-center justify-between px-3 py-1.5 text-[12px]">
                  <div className="flex-1 min-w-0">
                    <span className="text-slate-900 font-medium">{c.full_name || '(no name)'}</span>
                    {c.customer_number && (
                      <span className="text-slate-400 ml-2">{c.customer_number}</span>
                    )}
                    <span className={`ml-2 ${c.email ? 'text-slate-600' : 'text-amber-600'}`}>
                      {c.email || '(no email — will be skipped)'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeOne(c._id)}
                    className="p-1 text-slate-400 hover:text-rose-600 rounded"
                    aria-label="Remove"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <div className="flex items-start gap-2">
          <Mail className="h-4 w-4 text-blue-600 mt-1" />
          <div>
            <h2 className="text-[15px] font-semibold text-slate-900">Compose email</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">
              Sends one email per recipient via your facility's Resend sender.
              Merge tags below are substituted per recipient.
            </p>
          </div>
        </div>

        {composeSource && composeSource.kind === 'custom_template' && (
          <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[12px] text-blue-700">
            Composing from custom template: <strong>{composeSource.name}</strong>.
            Edit anything below or pick recipients to send.
          </div>
        )}

        <div>
          <label className="block text-[12px] font-medium text-slate-700 mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="A note from your storage facility"
            className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-[12px] font-medium text-slate-700">
              Body
            </label>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setBodyMode('plain')}
                className={`px-3 py-0.5 text-[11px] font-medium ${
                  bodyMode === 'plain'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                Plain text
              </button>
              <button
                type="button"
                onClick={() => setBodyMode('html')}
                className={`px-3 py-0.5 text-[11px] font-medium ${
                  bodyMode === 'html'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                HTML
              </button>
            </div>
          </div>
          <textarea
            ref={bodyRef}
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            rows={10}
            placeholder={
              bodyMode === 'html'
                ? '<p>Hi {{tenant_name}},</p>\n<p>...</p>'
                : 'Hi {{tenant_name}},\n\nLine breaks and blank lines render correctly.\n\nThanks,\nYour Storage Team'
            }
            className={`w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-blue-500 ${
              bodyMode === 'html' ? 'font-mono' : ''
            }`}
          />
          <p className="mt-1 text-[11px] text-slate-400">
            {bodyMode === 'plain'
              ? 'Type like a normal email — line breaks become paragraphs automatically.'
              : 'Paste raw HTML. Tags pass through unchanged.'}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
            <span className="text-[11px] text-slate-400">Insert merge tag:</span>
            {MERGE_TAGS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => insertTag(t)}
                className="text-[11px] px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded font-mono"
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[12px] font-medium text-slate-700 mb-1">
            Plain-text version (optional)
          </label>
          <textarea
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            rows={4}
            placeholder="Leave blank to auto-generate from HTML."
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Attachments — same files go to every recipient. Files are
            base64-encoded in the browser so they ride along with the
            JSON send payload. Cap is 8 MB per file (UI-side guard) and
            Resend will reject any send over 40 MB total. */}
        <div>
          <label className="block text-[12px] font-medium text-slate-700 mb-1">
            Attachments (optional)
          </label>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="file"
              multiple
              onChange={onAttachmentPick}
              className="text-[12px] text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-[12px] file:font-medium file:text-slate-700 hover:file:bg-slate-200"
            />
            <span className="text-[11px] text-slate-400">Up to 8 MB each. Same files go to every recipient.</span>
          </div>
          {attachmentError && (
            <div className="text-[12px] text-rose-600 mb-2">{attachmentError}</div>
          )}
          {attachments.length > 0 && (
            <ul className="space-y-1">
              {attachments.map((a, i) => (
                <li key={`${a.filename}-${i}`} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5">
                  <span className="text-[12px] text-slate-700 truncate">
                    <span className="font-medium">{a.filename}</span>
                    <span className="ml-2 text-slate-400">({(a.size / 1024).toFixed(0)} KB)</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    className="text-[11px] text-slate-400 hover:text-rose-600"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            onClick={onPreview}
            disabled={!canPreview}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md text-[13px] font-medium"
          >
            {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
            Preview
          </button>
          <button
            onClick={openConfirm}
            disabled={!canSend}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md text-[13px] font-medium"
          >
            <Send className="h-3.5 w-3.5" />
            {recipientCount > 0
              ? `Send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`
              : 'Send'}
          </button>
        </div>

        {previewError && (
          <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {previewError}
          </div>
        )}

        {sendResult && (
          <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-800 flex items-start gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 mt-0.5" />
            <span>
              Sent to <strong>{sendResult.sent_count}</strong> of <strong>{sendResult.recipient_count}</strong> recipients.
              {sendResult.failed_count > 0 && <> {sendResult.failed_count} failed (see history).</>}
            </span>
          </div>
        )}
      </div>

      {/* Preview panel */}
      {preview && preview.sample_recipient && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="text-[14px] font-semibold text-slate-900 mb-1">Sample render</h3>
          <p className="text-[12px] text-slate-500 mb-3">
            Showing how this email renders for{' '}
            <strong>{preview.sample_recipient.full_name}</strong>{' '}
            ({preview.sample_recipient.email})
            {preview.sample_recipient.unit_number && <> · Unit {preview.sample_recipient.unit_number}</>}.
          </p>
          <div className="text-[12px] text-slate-500 mb-1">Subject</div>
          <div className="text-[13px] text-slate-900 mb-3 px-3 py-2 bg-slate-50 rounded border border-slate-200">
            {preview.sample_subject || <em className="text-slate-400">(empty)</em>}
          </div>
          <div className="text-[12px] text-slate-500 mb-1">Rendered HTML</div>
          <div
            className="prose prose-sm max-w-none text-[13px] text-slate-800 px-4 py-3 bg-slate-50 rounded border border-slate-200"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: preview.sample_body_html || '' }}
          />
        </div>
      )}

      </>)}

      {/* History — also served standalone as the Email log tab */}
      {view !== 'compose' && (<>
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-3">
          <History className="h-4 w-4 text-slate-400" />
          <h3 className="text-[14px] font-semibold text-slate-900">Recent sends</h3>
        </div>
        {resendBanner && (
          <div
            className={`mb-3 rounded-md border px-3 py-2 text-[12px] flex items-start gap-2 ${
              resendBanner.kind === 'ok'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-rose-50 border-rose-200 text-rose-700'
            }`}
          >
            {resendBanner.kind === 'ok'
              ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5" />
              : <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
            }
            {resendBanner.text}
          </div>
        )}
        {tracking && (tracking.tracking === 'not_receiving' || tracking.tracking === 'stalled') && (
          <div className="mb-3 rounded-md border px-3 py-2 text-[12px] flex items-start gap-2 bg-amber-50 border-amber-200 text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              {tracking.detail}{' '}
              <a href="/settings?tab=communication&view=settings" className="underline font-medium">
                Set up delivery tracking
              </a>
            </span>
          </div>
        )}
        {tracking && tracking.tracking === 'active' && tracking.open_tracking === 'never_seen' && (
          <p className="mb-3 text-[11.5px] text-slate-400">
            Delivery is confirmed, but no email has ever been reported as seen. Turn on Open
            tracking for the sending domain in Resend and the Seen column starts filling.
          </p>
        )}
        {historyLoading && <p className="text-[12px] text-slate-400">Loading...</p>}
        {!historyLoading && history.length === 0 && (
          <p className="text-[12px] text-slate-500">No bulk emails sent yet.</p>
        )}
        {!historyLoading && history.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="text-slate-400 uppercase tracking-wider text-[11px]">
                <tr className="border-b border-slate-200">
                  <th className="py-2 pr-4 text-left font-medium">Sent at</th>
                  <th className="py-2 pr-4 text-left font-medium">Subject</th>
                  <th className="py-2 pr-4 text-right font-medium">Recipients</th>
                  <th className="py-2 pr-4 text-right font-medium">Delivered</th>
                  <th className="py-2 pr-4 text-right font-medium" title="Opened by the recipient (needs Open tracking on in Resend)">Seen</th>
                  <th className="py-2 pr-4 text-right font-medium">Bounced</th>
                  <th className="py-2 pr-4 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((h) => {
                  // Delivered: webhook-confirmed delivery. When the
                  // Resend webhook hasn't fired yet (or isn't wired —
                  // legacy rows), delivered_count is undefined and we
                  // fall back to sent_count ("we asked Resend to send,
                  // outcome pending"). The pending dash is shown when
                  // we have a meaningful sent_count but no webhook
                  // data yet, so the operator can tell which rows
                  // are still in flight.
                  const hasDeliveryData = typeof h.delivered_count === 'number';
                  const deliveredDisplay = hasDeliveryData
                    ? h.delivered_count
                    : (h.sent_count ?? 0);
                  const bouncedDisplay = h.bounced_count ?? 0;
                  // Show "—" when the row has sent successfully but
                  // we haven't gotten any delivery webhook yet.
                  const pendingDelivery = hasDeliveryData
                    && (h.sent_count ?? 0) > 0
                    && h.delivered_count === 0
                    && bouncedDisplay === 0;
                  return (
                    <tr key={h._id || h.id}>
                      <td className="py-2 pr-4 text-slate-700">{formatDateTime(h.sent_at, timeZone)}</td>
                      <td className="py-2 pr-4 text-slate-900">
                        {/* The table now shows BOTH manual bulk runs and
                            rule-fired automated sends — an operator asking
                            "did the invoice go out?" previously saw nothing
                            here, because automated mail lived in a different
                            collection. The badge says which is which. */}
                        {h.kind === 'automated' && (
                          <span
                            className="mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                            title={h.segment ? `Sent automatically by the "${h.segment}" rule` : 'Sent automatically by a rule'}
                          >
                            auto
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => openBody(h)}
                          className="text-left hover:underline hover:text-blue-700"
                          title="View the email as it was sent"
                        >
                          {h.subject}
                        </button>
                        {h.kind === 'automated' && h.to && (
                          <span className="block text-[11px] text-slate-400">{h.to}</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right text-slate-700">{h.recipient_count ?? 0}</td>
                      <td className="py-2 pr-4 text-right text-emerald-700 font-medium">
                        {pendingDelivery
                          ? <span className="inline-flex items-center gap-1 text-slate-400 font-normal" title="Waiting for Resend delivery confirmation">
                              <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />pending
                            </span>
                          : deliveredDisplay}
                      </td>
                      <td className={`py-2 pr-4 text-right font-medium ${(h.opened_count ?? 0) > 0 ? 'text-sky-700' : 'text-slate-400'}`}>{h.opened_count ?? 0}</td>
                      <td className={`py-2 pr-4 text-right font-medium ${bouncedDisplay > 0 ? 'text-rose-700' : 'text-slate-400'}`}>{bouncedDisplay}</td>
                      <td className="py-2 pr-4 text-right">
                        {(h.failed_count ?? 0) > 0 && (
                          <button
                            onClick={() => onResendFailed(h)}
                            disabled={resendingId === h._id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                            title={`Resend to the ${h.failed_count} failed recipient${h.failed_count === 1 ? '' : 's'}`}
                          >
                            {resendingId === h._id
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <RefreshCw className="h-3 w-3" />
                            }
                            Resend failed
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resend-failed review modal */}
      {resendModal && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-slate-500" />
                <h3 className="text-[15px] font-semibold text-slate-900">
                  Review before resend
                </h3>
              </div>
              <button
                onClick={() => setResendModal(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-4 space-y-4 text-[13px]">
              {resendModalLoading || resendModal.loading ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading preview...
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Subject</div>
                    <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-slate-900">
                      {resendModal.sample_subject || resendModal.parent_subject}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                      Sample render (first recipient)
                    </div>
                    <div
                      className="px-4 py-3 rounded-md bg-slate-50 border border-slate-200 text-slate-800 prose-sm max-w-none"
                      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                      dangerouslySetInnerHTML={{ __html: resendModal.sample_body_html || '' }}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11px] uppercase tracking-wider text-slate-400">
                        Recipients ({resendModal.recipient_count || 0})
                      </div>
                      {resendModal.skipped_synthetic_count > 0 && (
                        <div className="text-[11px] text-amber-700">
                          {resendModal.skipped_synthetic_count} synthetic placeholder{resendModal.skipped_synthetic_count === 1 ? '' : 's'} will be skipped
                        </div>
                      )}
                    </div>
                    <div className="rounded-md border border-slate-200 max-h-64 overflow-y-auto">
                      <div className="overflow-x-auto">
                        <table className="w-full text-[12px]">
                          <thead className="bg-slate-50 text-slate-500 sticky top-0">
                            <tr>
                              <th className="px-3 py-1.5 text-left font-medium">Name</th>
                              <th className="px-3 py-1.5 text-left font-medium">Email</th>
                              <th className="px-3 py-1.5 text-left font-medium">Unit</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(resendModal.recipients || []).map((r, i) => (
                              <tr key={r.customer_id || i}>
                                <td className="px-3 py-1.5 text-slate-900">{r.full_name || '—'}</td>
                                <td className="px-3 py-1.5 text-slate-700">{r.email || '—'}</td>
                                <td className="px-3 py-1.5 text-slate-600">{r.unit_number || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {resendModal.skipped_synthetic_count > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-amber-700 mb-2">
                        Skipped (synthetic placeholder emails)
                      </div>
                      <div className="rounded-md border border-amber-200 bg-amber-50 p-2 space-y-1">
                        {(resendModal.skipped_synthetic || []).map((r, i) => (
                          <div key={r.customer_id || i} className="text-[12px] text-amber-800">
                            <span className="font-medium">{r.full_name || '—'}</span>
                            <span className="text-amber-600 ml-2">{r.email}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => setResendModal(null)}
                disabled={!!resendingId}
                className="px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-100 rounded-md disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirmResend}
                disabled={!!resendingId || resendModalLoading || (resendModal.recipient_count || 0) === 0}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-[13px] font-medium"
              >
                {resendingId
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Send className="h-3.5 w-3.5" />
                }
                {resendingId ? 'Sending...' : `Send to ${resendModal.recipient_count || 0}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-[15px] font-semibold text-slate-900 mb-2">Send this email?</h3>
            <p className="text-[13px] text-slate-600 mb-4">
              You're about to send to <strong>{recipientCount}</strong> recipient
              {recipientCount === 1 ? '' : 's'}. This action cannot be undone.
            </p>
            {sendError && (
              <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-start gap-2 mb-3">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {sendError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={sending}
                className="px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-100 rounded-md disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onSend}
                disabled={sending}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-[13px] font-medium"
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {sending ? 'Sending...' : 'Send now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* What was actually sent. Rendered in a sandboxed iframe: this is
          stored HTML that came from a template, and injecting it straight
          into the page would let template content run script in the operator's
          session. */}
      {bodyView && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setBodyView(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 px-5 py-3 border-b border-slate-100">
              <div className="min-w-0">
                <p className="text-[14px] font-semibold text-slate-900 truncate">
                  {bodyView.subject || 'Email'}
                </p>
                <p className="text-[12px] text-slate-400">
                  {bodyView.to ? `To ${bodyView.to}` : 'Bulk send'}
                  {bodyView.rule_key ? ` · ${bodyView.rule_key}` : ''}
                </p>
              </div>
              <button
                onClick={() => setBodyView(null)}
                className="text-slate-400 hover:text-slate-600 text-[13px]"
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-auto p-5">
              {bodyView.loading ? (
                <p className="text-[13px] text-slate-400">Loading…</p>
              ) : bodyView.error ? (
                <p className="text-[13px] text-red-500">{bodyView.error}</p>
              ) : !bodyView.body_available ? (
                <p className="text-[13px] text-slate-500">
                  This email was sent before the content was recorded, so only
                  the subject and delivery status are available for it. Emails
                  sent from now on keep their full contents here.
                </p>
              ) : bodyView.channel === 'sms' ? (
                <pre className="text-[13px] whitespace-pre-wrap text-slate-800">
                  {bodyView.body}
                </pre>
              ) : (
                <SandboxedHtmlFrame
                  title="Email contents"
                  html={bodyView.body}
                  className="w-full h-[60vh] border border-slate-200 rounded-lg bg-white"
                />
              )}
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
};

export default BulkEmail;
